import { IconInfoCircle } from "@tabler/icons-react";
import { Button, Modal, TitleBar } from "@tredespace/ui/widgets";
import type { JSX } from "react";

/** Matches the layer the library's own dialog cores use. */
const MODAL_LAYER = 2000;

export type AboutDialogProps = Readonly<{ onDismiss: () => void }>;

const KINDS = [
  { kind: "runtime", title: "Runtime" },
  { kind: "build", title: "Build and tooling" },
] as const;

/**
 * Version, authorship and the dependency list, all injected from package.json.
 *
 * `Modal` supplies only the dimmed backdrop and centring, so the window surface
 * — border, background, shadow — belongs to the dialog, as in the library's own
 * dialog cores. Authored in dark utilities; the stylesheet remaps them for light.
 */
export function AboutDialog({ onDismiss }: AboutDialogProps): JSX.Element {
  return (
    <Modal z={MODAL_LAYER} onKeyDown={(event) => event.key === "Escape" && onDismiss()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="About HAKI Spira"
        className="flex max-h-[80vh] w-[34rem] max-w-[92vw] flex-col border border-slate-600 bg-slate-900 shadow-black/50 shadow-xl"
      >
        <TitleBar icon={<IconInfoCircle size={16} className="shrink-0 text-blue-400" />}>HAKI Spira</TitleBar>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 text-xs">
          <p className="text-sm font-semibold">
            HAKI Spira <span className="font-mono text-slate-400">v{__APP_VERSION__}</span>
          </p>
          <p className="mt-1 leading-relaxed text-slate-400">
            Scaffold planner for HAKI Universal Aluminium S4. Component data from the RISE product certificate
            C900677 and the HAKI 2024 component list.
          </p>
          <p className="mt-2">Created by Vegar Ringdal</p>

          {KINDS.map((group) => (
            <section key={group.kind} className="mt-4">
              <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {group.title}
              </h3>
              <ul className="grid grid-cols-[1fr_auto] gap-x-4">
                {__APP_DEPENDENCIES__
                  .filter((dep) => dep.kind === group.kind)
                  .map((dep) => (
                    <li key={dep.name} className="contents">
                      <span className="truncate border-b border-slate-800 py-0.5">{dep.name}</span>
                      <span className="border-b border-slate-800 py-0.5 text-right font-mono text-slate-400">
                        {dep.version}
                      </span>
                    </li>
                  ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="flex justify-end gap-1.5 border-t border-slate-800 px-3 py-2">
          <Button onClick={onDismiss}>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
